import { Responsive, WidthProvider } from 'react-grid-layout';
import type { Layout, Layouts } from 'react-grid-layout';
import {
  BREAKPOINTS,
  GRID_COLUMNS,
  GRID_CONTAINER_PADDING,
  GRID_MARGIN,
  GRID_ROW_HEIGHT
} from '../config/appConfig';
import type { WindowConfig, WindowInstance } from './types';
import { WindowFrame } from './WindowFrame';
import { QuotesWindow } from '../windows/quotes/QuotesWindow';
import { GraphWindow } from '../windows/graph/GraphWindow';
import { HeatMapWindow } from '../windows/heatmap/HeatMapWindow';
import { NewsWindow } from '../windows/news/NewsWindow';
import { NewsDetailWindow } from '../windows/news/NewsDetailWindow';
import { QuoteWindow } from '../windows/quote/QuoteWindow';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface WorkspaceGridProps {
  windows: WindowInstance[];
  layouts: Layouts;
  onLayoutsChange: (layouts: Layouts) => void;
  onRemoveWindow: (id: string) => void;
  onUpdateWindowConfig: (id: string, updater: (config: WindowConfig) => WindowConfig) => void;
  onFocusWindow: (id: string) => void;
  onOpenGraph: (symbol: string) => void;
  onOpenNewsDetail: (newsId: string) => void;
}

export function WorkspaceGrid({
  windows,
  layouts,
  onLayoutsChange,
  onRemoveWindow,
  onUpdateWindowConfig,
  onFocusWindow,
  onOpenGraph,
  onOpenNewsDetail
}: WorkspaceGridProps) {
  return (
    <div className="workspace">
      <ResponsiveGridLayout
        className="workspace__grid"
        layouts={layouts}
        breakpoints={BREAKPOINTS}
        cols={GRID_COLUMNS}
        rowHeight={GRID_ROW_HEIGHT}
        margin={GRID_MARGIN}
        containerPadding={GRID_CONTAINER_PADDING}
        draggableHandle=".terminal-window__header"
        draggableCancel=".no-window-drag, input, button, select, textarea, canvas, a"
        compactType={null}
        preventCollision={false}
        allowOverlap
        isBounded
        isDraggable
        isResizable
        useCSSTransforms
        resizeHandles={['se', 'e', 's', 'sw', 'w', 'ne', 'nw', 'n']}
        onDragStart={(_layout: Layout[], _oldItem: Layout, newItem: Layout) => onFocusWindow(newItem.i)}
        onResizeStart={(_layout: Layout[], _oldItem: Layout, newItem: Layout) => onFocusWindow(newItem.i)}
        onLayoutChange={(_current: Layout[], all: Layouts) => onLayoutsChange(all)}
      >
        {windows.map((window) => (
          <div
            key={window.id}
            className="workspace__cell"
            style={{ zIndex: window.zIndex }}
            onClickCapture={() => onFocusWindow(window.id)}
          >
            <WindowFrame window={window} onClose={onRemoveWindow}>
              {renderWindow(window, onUpdateWindowConfig, onOpenGraph, onOpenNewsDetail)}
            </WindowFrame>
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
}

function renderWindow(
  window: WindowInstance,
  onUpdate: WorkspaceGridProps['onUpdateWindowConfig'],
  onOpenGraph: WorkspaceGridProps['onOpenGraph'],
  onOpenNewsDetail: WorkspaceGridProps['onOpenNewsDetail']
) {
  switch (window.kind) {
    case 'quotes':
      return (
        <QuotesWindow
          window={window as WindowInstance<any>}
          onConfigChange={(updater) => onUpdate(window.id, updater)}
          onOpenGraph={onOpenGraph}
        />
      );

    case 'quote':
      return (
        <QuoteWindow
          window={window as WindowInstance<any>}
          onConfigChange={(updater) => onUpdate(window.id, updater)}
          onOpenGraph={onOpenGraph}
        />
      );

    case 'graph':
      return (
        <GraphWindow
          window={window as WindowInstance<any>}
          onConfigChange={(updater) => onUpdate(window.id, updater)}
        />
      );

    case 'heatmap':
      return <HeatMapWindow window={window as WindowInstance<any>} />;

    case 'news':
      return (
        <NewsWindow
          window={window as WindowInstance<any>}
          onConfigChange={(updater) => onUpdate(window.id, updater)}
          onOpenNewsDetail={onOpenNewsDetail}
        />
      );

    case 'newsDetail':
      return <NewsDetailWindow window={window as WindowInstance<any>} />;
  }
}